import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import '../styles/ServicePage.css';
import { setPageMeta } from '../utils/seo';

const PerformanceManagement = () => {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
    setPageMeta(
      'Performance Management | HRCONSOL',
      'Goal setting, 360 feedback, competency assessment and rewards frameworks to drive accountability and excellence.'
    );
  }, []);

  return (
    <div className="service-page">
      <div className="service-hero">
        <div className="container">
          <h1>Performance Management – Driving Excellence and Accountability</h1>
          <p className="subtitle">HRCONSOL's Performance Management solutions provide customized frameworks and tools to track, enhance, and reward employee performance, driving long-term organizational success.</p>
        </div>
      </div>

      <div className="container">
        <div className="service-content">
          <div className="service-section">
            <p>Effective performance management is essential for building high-performing teams and achieving business goals. HRCONSOL's <strong>Performance Management Systems</strong> help organizations set clear expectations, provide continuous feedback, and create cultures of accountability and excellence.</p>
            <p>We design and implement performance frameworks that align individual contributions with organizational objectives.</p>
          </div>

          <div className="service-section">
            <h2>Scope of Services</h2>
            <p>Our Performance Management Solutions Include:</p>
            <div className="process-steps">
              <div className="step">
                <h3><i className="fas fa-bullseye"></i> Goal Setting & KPIs</h3>
                <p>Defining SMART goals, KPIs, and performance metrics aligned with business strategy.</p>
              </div>
              <div className="step">
                <h3><i className="fas fa-chart-line"></i> Performance Appraisal Systems</h3>
                <p>Designing annual, quarterly, or continuous appraisal processes.</p>
              </div>
              <div className="step">
                <h3><i className="fas fa-comments"></i> 360-Degree Feedback</h3>
                <p>Multi-source feedback from peers, managers, and subordinates.</p>
              </div>
              <div className="step">
                <h3><i className="fas fa-award"></i> Competency Assessment</h3>
                <p>Evaluating skills, behaviors, and leadership competencies.</p>
              </div>
              <div className="step">
                <h3><i className="fas fa-trophy"></i> Rewards & Recognition Programs</h3>
                <p>Linking performance to bonuses, incentives, and career growth.</p>
              </div>
              <div className="step">
                <h3><i className="fas fa-user-check"></i> Performance Improvement Plans</h3>
                <p>Supporting underperformers with structured development plans.</p>
              </div>
              <div className="step">
                <h3><i className="fas fa-laptop"></i> Digital Performance Tools</h3>
                <p>Cloud-based HRMS integration for real-time tracking and reporting.</p>
              </div>
              <div className="step">
                <h3><i className="fas fa-chalkboard-teacher"></i> Manager Training</h3>
                <p>Training leaders on effective feedback, coaching, and performance conversations.</p>
              </div>
            </div>
          </div>

          <div className="service-section">
            <h2>Industries We Serve</h2>
            <ul className="benefits-list">
              <li><i className="fas fa-laptop-code"></i> IT & Software Companies</li>
              <li><i className="fas fa-university"></i> Banking & Financial Services</li>
              <li><i className="fas fa-industry"></i> Manufacturing & Production</li>
              <li><i className="fas fa-shopping-cart"></i> Retail & E-Commerce</li>
              <li><i className="fas fa-pills"></i> Healthcare & Pharmaceuticals</li>
              <li><i className="fas fa-graduation-cap"></i> Education & Training</li>
              <li><i className="fas fa-truck"></i> Logistics & Supply Chain</li>
              <li><i className="fas fa-phone"></i> Telecommunications</li>
              <li><i className="fas fa-leaf"></i> NGOs & Development Sector</li>
              <li><i className="fas fa-hotel"></i> Hospitality & Services</li>
            </ul>
          </div>

          <div className="service-section">
            <h2>Why Choose HRCONSOL Performance Management?</h2>
            <p className="track-record-intro"><strong>Results That Matter:</strong></p>
            <ul className="benefits-list">
              <li>
                <span className="benefit-heading">200+ Organizations Served</span>
                <span className="benefit-subheading">implementing effective performance systems</span>
              </li>
              <li>
                <span className="benefit-heading">35% Increase in Productivity</span>
                <span className="benefit-subheading">through goal alignment and accountability</span>
              </li>
              <li>
                <span className="benefit-heading">90% Manager Adoption Rate</span>
                <span className="benefit-subheading">with user-friendly systems and training</span>
              </li>
              <li>
                <span className="benefit-heading">Customized Frameworks</span>
                <span className="benefit-subheading">tailored to your company size, culture, and goals</span>
              </li>
              <li>
                <span className="benefit-heading">Technology Integration</span>
                <span className="benefit-subheading">seamless HRMS and dashboard integration</span>
              </li>
              <li>
                <span className="benefit-heading">Ongoing Support</span>
                <span className="benefit-subheading">training, coaching, and system optimization</span>
              </li>
            </ul>
          </div>

          <div className="service-section">
            <h2>Our Implementation Process</h2>
            <div className="process-steps">
              <div className="step">
                <h3>Step 1: Needs Assessment</h3>
                <p>Understanding current practices, challenges, and organizational goals.</p>
              </div>
              <div className="step">
                <h3>Step 2: Framework Design</h3>
                <p>Creating customized performance management frameworks and tools.</p>
              </div>
              <div className="step">
                <h3>Step 3: System Setup</h3>
                <p>Configuring digital tools, templates, and documentation.</p>
              </div>
              <div className="step">
                <h3>Step 4: Training & Rollout</h3>
                <p>Training managers and employees on the new system.</p>
              </div>
              <div className="step">
                <h3>Step 5: Review & Optimization</h3>
                <p>Monitoring effectiveness and making continuous improvements.</p>
              </div>
            </div>
          </div>

          <div className="service-section">
            <h2>Packages Details</h2>
            
            <div className="process-steps">
              <div className="step package-card">
                <h3>1. BASIC</h3>
                <p>Small to Medium Businesses (Up to 100 Employees)</p>
                <ul className="package-list">
                  <li>Fee: PKR 150,000 (one-time setup)</li>
                  <li>Performance appraisal framework</li>
                  <li>Goal-setting templates</li>
                  <li>Manager training (1 session)</li>
                  <li>Documentation & guidelines</li>
                </ul>
                <p className="package-note">Perfect for establishing foundational performance practices.</p>
                <button onClick={() => navigate('/#contact')} className="package-button">Choose Plan</button>
              </div>

              <div className="step package-card">
                <h3>2. PROFESSIONAL</h3>
                <p>Growing Organizations (101-500 Employees)</p>
                <ul className="package-list">
                  <li>Fee: PKR 400,000 (setup + 3-month support)</li>
                  <li>Comprehensive performance system</li>
                  <li>360-degree feedback mechanism</li>
                  <li>HRMS integration</li>
                  <li>Manager & employee training (3 sessions)</li>
                  <li>Quarterly review support</li>
                </ul>
                <p className="package-note">Ideal for mid-size companies seeking robust performance management.</p>
                <button onClick={() => navigate('/#contact')} className="package-button">Choose Plan</button>
              </div>

              <div className="step package-card">
                <h3>3. ENTERPRISE</h3>
                <p>Large Corporations (500+ Employees)</p>
                <ul className="package-list">
                  <li>Fee: Custom (based on requirements)</li>
                  <li>End-to-end performance management</li>
                  <li>Multi-level competency frameworks</li>
                  <li>Advanced analytics & dashboards</li>
                  <li>Rewards & recognition programs</li>
                  <li>Annual support & optimization</li>
                  <li>Executive coaching</li>
                </ul>
                <button onClick={() => navigate('/#contact')} className="package-button">Choose Plan</button>
              </div>
            </div>
          </div>

          <div className="service-cta">
            <h2>Ready to Enhance Performance?</h2>
            <p>Let HRCONSOL help you build a culture of high performance and accountability.</p>
            <button onClick={() => navigate('/#contact')} className="cta-button">Contact Us</button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PerformanceManagement;
